// AntiTheftVerificationPage.js
import React from 'react';
import { Box, Typography, Container, Breadcrumbs, Link as MuiLink, List, ListItem, ListItemText } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

const AntiTheftVerificationPage = () => {
  const breadcrumbs = [
    { name: 'Home', path: '/' },
    { name: 'Legal & Company', path: '/legal' },
    { name: 'Anti-Theft & Verification', path: '/legal/anti-theft-policy' },
  ];

  const measures = [
    {
      title: 'Rigorous Seller Verification',
      description: 'All sellers on our platform undergo a thorough verification process to confirm their identity and legitimacy. This includes document checks and, where applicable, physical site inspections.',
      details: [
        'KYC (Know Your Customer) procedures.',
        'Business registration verification.',
        'Cross-referencing with industry blacklists.',
      ],
    },
    {
      title: 'Material Traceability',
      description: 'We encourage and, for certain high-value materials, require documentation that proves the legitimate origin of the scrap. This helps prevent the trade of stolen goods.',
      details: [
        'Certificates of origin or ownership.',
        'Transaction history tracking.',
        'Integration with relevant databases (e.g., VIN checks for automotive scrap).',
      ],
    },
    {
      title: 'Reporting Suspicious Activity',
      description: 'Our platform provides clear mechanisms for users to report any suspicious listings or activities. All reports are investigated promptly and confidentially.',
      details: [
        'Dedicated reporting channels.',
        'Collaboration with law enforcement agencies.',
        'Immediate suspension of suspicious accounts/listings.',
      ],
    },
    {
      title: 'Partnerships with Authorities',
      description: 'We actively collaborate with local and international law enforcement agencies, industry bodies, and anti-theft organizations to combat illegal scrap trade.',
      details: [
        'Information sharing agreements (where legally permissible).',
        'Participation in anti-theft initiatives.',
        'Training for our staff on identifying illicit materials.',
      ],
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', p: 2 }}>
      <Container sx={{ py: { xs: 4, md: 6 }, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
        {/* Breadcrumbs */}
        <Box sx={{ mb: { xs: 2, md: 4 }, px: { xs: 2, md: 0 } }}>
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
            {breadcrumbs.map((crumb, index) => (
              crumb.path ? (
                <MuiLink component={RouterLink} to={crumb.path} key={index} underline="hover" color="text.secondary" variant="body2">
                  {crumb.name}
                </MuiLink>
              ) : (
                <Typography key={index} color="text.primary" variant="body2">{crumb.name}</Typography>
              )
            ))}
          </Breadcrumbs>
        </Box>

        <Typography variant="h3" align="center" fontWeight={700} sx={{ mb: { xs: 4, md: 6 }, color: 'text.primary' }}>
          Anti-Theft & Verification
        </Typography>

        <Typography variant="body1" align="center" sx={{ mb: { xs: 4, md: 6 }, maxWidth: '800px', mx: 'auto', color: 'text.secondary' }}>
          Ensuring the legitimacy and ethical sourcing of materials is a top priority. We employ comprehensive anti-theft and verification measures to protect our users and the integrity of the scrap trade.
        </Typography>

        <Box sx={{ mt: 6 }}>
          {measures.map((measure, index) => (
            <Box
              key={index}
              sx={{
                mb: 6,
                p: { xs: 3, md: 5 },
                borderRadius: 4,
                background: 'background.paper',
                boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                border: '1px solid',
                borderColor: 'grey.200',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 16px 32px rgba(0,0,0,0.12)',
                },
              }}
            >
              <Typography variant="h4" fontWeight={700} sx={{ mb: 2, color: 'primary.dark' }}>
                {measure.title}
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, color: 'text.primary' }}>
                {measure.description}
              </Typography>

              <List dense disablePadding>
                {measure.details.map((detail, detailIndex) => (
                  <ListItem key={detailIndex} disablePadding sx={{ py: 0.5 }}>
                    <ListItemText primary={detail} primaryTypographyProps={{ variant: 'body1', color: 'text.secondary' }} />
                  </ListItem>
                ))}
              </List>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default AntiTheftVerificationPage;
